import React from 'react';
import { CAR_CLASSES, LAP_OPTIONS, TYRES } from '../../data/tracks';

/* ══════════════════════════════════════
   PER-TRACK SETTINGS (owner, 31 Oct)
   A round with several tracks can race each one in its own car class and
   over its own number of laps. Saved on the event as
     trackSettings: [ { track, carClass, tyre, laps } ]   (selected tracks only)
   A blank value means "same as the round" (the round's Laps / Car Class),
   so a round where every track is the same needs nothing set here.
   ══════════════════════════════════════ */

/** The rows to save: one per selected track that has its own setting. */
export function cleanTrackSettings(tracks, settings, titleId) {
  const byTrack = new Map((settings || []).map(s => [s.track, s]));
  return (tracks || []).map((track) => {
    const s = byTrack.get(track) || {};
    const carClass = titleId === 'granTurismo7' && s.carClass ? s.carClass : null;
    const tyre = titleId === 'granTurismo7' && s.tyre ? s.tyre : null;
    const laps = s.laps ? parseInt(s.laps, 10) : null;
    return carClass || tyre || laps ? { track, carClass, tyre, laps } : null;
  }).filter(Boolean);
}

export default function TrackSettings({ titleId, tracks, settings, onChange, defaultLaps, defaultCarClass, defaultTyre }) {
  if (!tracks || tracks.length < 1) return null;
  const isGT7 = titleId === 'granTurismo7';
  const byTrack = new Map((settings || []).map(s => [s.track, s]));

  const setFor = (track, patch) => {
    const next = tracks.map((t) => {
      const cur = byTrack.get(t) || { track: t, carClass: null, tyre: null, laps: null };
      return t === track ? { ...cur, ...patch } : cur;
    });
    onChange(next);
  };

  const lapsDefault = defaultLaps ? `Round (${defaultLaps})` : 'Round (N/A)';
  const classDefault = defaultCarClass ? `Round (${defaultCarClass})` : 'Round (any)';
  const tyreDefault = defaultTyre ? `Round (${defaultTyre})` : 'Round (any)';

  return (
    <div style={{ marginTop: 10 }}>
      <div className="form-label" style={{ marginBottom: 6 }}>Settings per track</div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Track</th>
              {isGT7 && <th style={{ width: 170 }}>Car Class</th>}
              {isGT7 && <th style={{ width: 190 }}>Tyres</th>}
              <th style={{ width: 140 }}>Laps</th>
            </tr>
          </thead>
          <tbody>
            {tracks.map((track) => {
              const s = byTrack.get(track) || {};
              return (
                <tr key={track}>
                  <td>{track}</td>
                  {isGT7 && (
                    <td>
                      <select className="form-select" value={s.carClass || ''}
                        onChange={e => setFor(track, { carClass: e.target.value || null })}>
                        <option value="">{classDefault}</option>
                        {CAR_CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </td>
                  )}
                  {isGT7 && (
                    <td>
                      <select className="form-select" value={s.tyre || ''}
                        onChange={e => setFor(track, { tyre: e.target.value || null })}>
                        <option value="">{tyreDefault}</option>
                        {TYRES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </td>
                  )}
                  <td>
                    <select className="form-select" value={s.laps || ''}
                      onChange={e => setFor(track, { laps: e.target.value ? parseInt(e.target.value, 10) : null })}>
                      <option value="">{lapsDefault}</option>
                      {LAP_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <span style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, display: 'block' }}>
        Leave a track on “Round” to use the round’s {isGT7 ? 'car class, tyres and laps' : 'laps'}.
      </span>
    </div>
  );
}
